// @ts-nocheck
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  fetchTeacherClassDetail,
  renameTeacherClass,
  updateTeacherClassStudents,
  moveStudentBetweenClasses,
  fetchTeacherSubjectsWithStudents,
} from '../../services/api';
import StudentNameOrderToggle from '../../components/StudentNameOrderToggle';
import PageBackNavigation from '../../components/PageBackNavigation';
import ResizableDualListBox from '../../components/ResizableDualListBox';
import { sortStudentsByName, useStudentNameOrder } from '../../utils/studentSort';

const sectionLabel = { fontSize: 13, fontWeight: 700, color: 'var(--ae-text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 };
const sectionCard = { background: 'var(--ae-surface)', border: '1px solid var(--ae-border)', borderRadius: 18, padding: 20, boxShadow: 'var(--ae-shadow-card)' };
const selectStyle = { border: '1px solid var(--ae-border)', borderRadius: 8, padding: '8px 10px', width: '100%', fontSize: 13, background: 'var(--ae-surface)', outline: 'none' };

export default function ClassManagePage() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo || null;

  const [loading, setLoading] = useState<any>(true);
  const [error, setError] = useState<any>('');
  const [classInfo, setClassInfo] = useState<any>(null);

  // All classes this teacher teaches (for move-student target)
  const [allClasses, setAllClasses] = useState<any>([]);

  // Name edit
  const [editingName, setEditingName] = useState<any>(false);
  const [newClassName, setNewClassName] = useState<any>('');
  const [nameSaving, setNameSaving] = useState<any>(false);
  const [nameError, setNameError] = useState<any>('');

  // Manage students
  const [manageStudents, setManageStudents] = useState<any>(false);
  const [allSchoolStudents, setAllSchoolStudents] = useState<any>([]);
  const [assignedIds, setAssignedIds] = useState<any>([]);
  const [selAvailable, setSelAvailable] = useState<any>([]);
  const [selAssigned, setSelAssigned] = useState<any>([]);
  const [studentSaving, setStudentSaving] = useState<any>(false);
  const [studentError, setStudentError] = useState<any>('');
  const [studentSuccess, setStudentSuccess] = useState<any>('');

  // Move student
  const [moveStudentId, setMoveStudentId] = useState<any>('');
  const [moveToClassId, setMoveToClassId] = useState<any>('');
  const [moveSaving, setMoveSaving] = useState<any>(false);
  const [moveError, setMoveError] = useState<any>('');
  const [moveSuccess, setMoveSuccess] = useState<any>('');

  const [nameOrder] = useStudentNameOrder();

  const loadClassDetail = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const detail = await fetchTeacherClassDetail(classId);
      setClassInfo(detail);
      setNewClassName(detail.class?.class_name || '');
      setAssignedIds((detail.students || []).map(s => s.student_id));
    } catch (e: any) {
      setError(e.message || 'Failed to load class detail');
    } finally {
      setLoading(false);
    }
  }, [classId]);

  useEffect(() => {
    loadClassDetail();
    // Load all teacher classes (for move target selector) and all school students
    fetchTeacherSubjectsWithStudents().then(data => {
      const classes = (data.subjects || []).flatMap(s => s.classes || []);
      setAllClasses(classes);
      // Unique students across all classes for the add-student picker
      const seen = new Set();
      const students = [];
      classes.forEach(cls => (cls.students || []).forEach(stu => {
        if (!seen.has(stu.student_id)) {
          seen.add(stu.student_id);
          students.push(stu);
        }
      }));
      setAllSchoolStudents(students);
    }).catch(() => {});
  }, [loadClassDetail]);

  const handleBack = () => {
    if (returnTo?.pathname) {
      navigate(returnTo.pathname, returnTo.state ? { state: returnTo.state } : undefined);
    } else {
      navigate('/teacher/home');
    }
  };

  const handleSaveName = async () => {
    setNameSaving(true);
    setNameError('');
    const trimmed = newClassName.trim();
    if (!trimmed) { setNameError('Class name cannot be empty.'); setNameSaving(false); return; }
    try {
      const updated = await renameTeacherClass(classId, trimmed);
      setClassInfo(prev => ({ ...prev, class: { ...prev.class, class_name: updated.class_name } }));
      setEditingName(false);
    } catch (e: any) {
      setNameError('Failed to save. Please try again.');
    } finally {
      setNameSaving(false);
    }
  };

  const handleSaveStudents = async () => {
    setStudentSaving(true);
    setStudentError('');
    setStudentSuccess('');
    const original = (classInfo?.students || []).map(s => s.student_id);
    const add_student_ids = assignedIds.filter(id => !original.includes(id));
    const remove_student_ids = original.filter(id => !assignedIds.includes(id));
    try {
      await updateTeacherClassStudents(classId, { add_student_ids, remove_student_ids });
      setStudentSuccess('Students updated.');
      await loadClassDetail();
      setManageStudents(false);
    } catch (e: any) {
      setStudentError('Failed to update students. Please try again.');
    } finally {
      setStudentSaving(false);
    }
  };

  const handleMoveStudent = async () => {
    setMoveSaving(true);
    setMoveError('');
    setMoveSuccess('');
    if (!moveStudentId || !moveToClassId) {
      setMoveError('Select a student and a destination class.');
      setMoveSaving(false);
      return;
    }
    try {
      await moveStudentBetweenClasses(classId, Number(moveStudentId), Number(moveToClassId));
      setMoveSuccess('Student moved successfully.');
      setMoveStudentId('');
      setMoveToClassId('');
      await loadClassDetail();
    } catch (e: any) {
      setMoveError('Failed to move student. Please try again.');
    } finally {
      setMoveSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--ae-text-muted)' }}>Loading...</div>;
  if (error) return <div style={{ padding: 40, color: 'var(--brand-red)', textAlign: 'center' }}>{error}</div>;
  if (!classInfo) return null;

  const cls = classInfo.class || {};
  const classStudents = sortStudentsByName(classInfo.students || [], nameOrder);
  const sortedSchoolStudents = sortStudentsByName(allSchoolStudents, nameOrder);
  const otherClasses = allClasses.filter(c => String(c.class_id) !== String(classId));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PageBackNavigation onBack={handleBack} />
      {/* Hero banner */}
      <section style={{ background: 'linear-gradient(135deg, #27459b 0%, #1e3a8a 100%)', color: '#ffffff', borderRadius: 18, padding: '24px 26px', boxShadow: '0 12px 32px rgba(30, 58, 138, 0.18)' }}>
        <div style={{ fontSize: 28, fontWeight: 800, marginBottom: 6 }}>Manage Class</div>
        <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.86)', lineHeight: 1.6 }}>
          Rename this class, add or remove students, or move a student to another one of your classes.
        </div>
      </section>

      {/* Class name */}
      <section style={sectionCard}>
        <div style={sectionLabel}>Class Name</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {editingName ? (
            <>
              <input
                value={newClassName}
                onChange={e => setNewClassName(e.target.value)}
                style={{ fontSize: 22, fontWeight: 700, color: 'var(--ae-primary-strong)', border: '1px solid var(--ae-border)', borderRadius: 8, padding: '6px 14px', minWidth: 200 }}
                maxLength={100}
                autoFocus
              />
              <button className="ae-btn-primary" onClick={handleSaveName} disabled={nameSaving}>
                {nameSaving ? 'Saving…' : 'Save'}
              </button>
              <button className="ae-btn-soft" onClick={() => { setEditingName(false); setNewClassName(cls.class_name || ''); setNameError(''); }} disabled={nameSaving}>
                Cancel
              </button>
              {nameError && <span style={{ color: 'var(--brand-red)', fontSize: 13 }}>{nameError}</span>}
            </>
          ) : (
            <>
              <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--ae-primary-strong)' }}>
                {cls.class_name || `Class ${cls.class_id}`}
              </div>
              <button className="ae-btn-soft" onClick={() => setEditingName(true)}>Rename</button>
            </>
          )}
        </div>
      </section>

      {/* Students */}
      <section style={sectionCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
          <div style={sectionLabel}>Students ({classStudents.length})</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <StudentNameOrderToggle />
            {!manageStudents && (
            <button
              className="ae-btn-primary"
              onClick={() => { setManageStudents(true); setAssignedIds(classStudents.map(s => s.student_id)); setSelAvailable([]); setSelAssigned([]); setStudentError(''); setStudentSuccess(''); }}
            >
              ✏️ Add / Remove Students
            </button>
          )}
          </div>
        </div>
        {classStudents.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {classStudents.map(s => (
              <div key={s.student_id} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--ae-surface-soft)', border: '1px solid var(--ae-border)', borderRadius: 20, padding: '6px 14px', fontSize: 13, color: 'var(--ae-text)' }}>
                <span style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--ae-primary)', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11, flexShrink: 0 }}>
                  {(s.first_name?.[0] || '?').toUpperCase()}
                </span>
                <span style={{ fontWeight: 600 }}>{s.first_name} {s.last_name}</span>
                <span style={{ color: 'var(--ae-text-muted)', fontSize: 12 }}>{s.email}</span>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: 'var(--ae-text-muted)', fontSize: 14 }}>No students yet.</div>
        )}
        {manageStudents && (
          <div style={{ borderTop: '1px solid var(--ae-border)', paddingTop: 20, marginTop: 20 }}>
            <div style={sectionLabel}>Add / Remove Students</div>
            <ResizableDualListBox
              leftTitle="Not in this class"
              leftItems={sortedSchoolStudents.filter(s => !assignedIds.includes(s.student_id))}
              leftSelectedIds={selAvailable}
              onLeftSelectionChange={setSelAvailable}
              renderLeftOption={s => `${s.first_name} ${s.last_name}${s.email ? ` (${s.email})` : ''}`}
              getLeftItemId={s => s.student_id}

              rightTitle="In this class"
              rightItems={sortedSchoolStudents.filter(s => assignedIds.includes(s.student_id))}
              rightSelectedIds={selAssigned}
              onRightSelectionChange={setSelAssigned}
              renderRightOption={s => `${s.first_name} ${s.last_name}${s.email ? ` (${s.email})` : ''}`}
              getRightItemId={s => s.student_id}

              onAdd={() => { setAssignedIds(ids => [...ids, ...selAvailable]); setSelAvailable([]); }}
              addDisabled={selAvailable.length === 0}

              onRemove={() => { setAssignedIds(ids => ids.filter(id => !selAssigned.includes(id))); setSelAssigned([]); }}
              removeDisabled={selAssigned.length === 0}

              defaultHeight={180}
              minHeight={130}
              minWidth={180}
            />
            {studentError && <div style={{ color: 'var(--brand-red)', fontSize: 13, marginTop: 10 }}>{studentError}</div>}
            {studentSuccess && <div style={{ color: 'var(--brand-green)', fontSize: 13, marginTop: 10 }}>{studentSuccess}</div>}
            <div className="ae-action-row" style={{ marginTop: 16 }}>
              <button className="ae-btn-primary" disabled={studentSaving} onClick={handleSaveStudents}>
                {studentSaving ? 'Saving…' : 'Save Changes'}
              </button>
              <button className="ae-btn-soft" disabled={studentSaving} onClick={() => setManageStudents(false)}>Cancel</button>
            </div>
          </div>
        )}
      </section>

      {/* Move student */}
      {otherClasses.length > 0 && classStudents.length > 0 && (
        <section style={sectionCard}>
          <div style={sectionLabel}>Move Student to Another Class</div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ae-text-muted)', marginBottom: 6 }}>Student</div>
              <select style={{ ...selectStyle, minWidth: 200 }} value={moveStudentId} onChange={e => setMoveStudentId(e.target.value)}>
                <option value="">Select student…</option>
                {classStudents.map(s => (
                  <option key={s.student_id} value={s.student_id}>{s.first_name} {s.last_name}</option>
                ))}
              </select>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ae-text-muted)', marginBottom: 6 }}>Move to</div>
              <select style={{ ...selectStyle, minWidth: 200 }} value={moveToClassId} onChange={e => setMoveToClassId(e.target.value)}>
                <option value="">Select destination class…</option>
                {otherClasses.map(c => (
                  <option key={c.class_id} value={c.class_id}>{c.class_name || `Class ${c.class_id}`}</option>
                ))}
              </select>
            </div>
            <button className="ae-btn-primary" disabled={moveSaving || !moveStudentId || !moveToClassId} onClick={handleMoveStudent}>
              {moveSaving ? 'Moving…' : 'Move Student'}
            </button>
          </div>
          {moveError && <div style={{ color: 'var(--brand-red)', fontSize: 13, marginTop: 10 }}>{moveError}</div>}
          {moveSuccess && <div style={{ color: 'var(--brand-green)', fontSize: 13, marginTop: 10 }}>{moveSuccess}</div>}
        </section>
      )}
      <PageBackNavigation onBack={handleBack} position="bottom" />
    </div>
  );
}
